import {Component, Element, h, Prop, Host, Method} from '@stencil/core';
import {ButtonSizes, ButtonVariants, IconsSizes} from '@/types';
import {handleRippleEffect} from '@/utils';

@Component({
    tag: 'joy-button',
    styleUrl: './button.scss',
    scoped: true,
})
export class JoyButton {
    @Element() el!: HTMLJoyButtonElement;
    /** Disabled attribute for buttons */
    @Prop({mutable: true}) disabled = false;
    /** Type attribute for buttons */
    @Prop({reflect: true}) type: 'button' | 'submit' | 'reset' = 'button';
    /** Set the href of your link */
    @Prop({reflect: true}) href?: string;
    /** If the link as a downloadable content */
    @Prop({reflect: true}) download: string | undefined;
    /** Native rel attribute for hyperlinks. See https://developer.mozilla.org/fr/docs/Web/HTML/Attributes/rel */
    @Prop({reflect: true}) rel?: string;
    /** Native target attribute for hyperlinks. */
    @Prop({reflect: true}) target?: '_blank' | '_self' | '_parent' | '_top';
    /** If the joy-button is supposed to submit a specific form without being included in the actual form tag, use this prop */
    @Prop({reflect: true}) form?: string;
    /** Button or Link color theme */
    @Prop({reflect: true}) variant: ButtonVariants = 'primary';
    /** Button or Link size */
    @Prop({reflect: true}) size: ButtonSizes = 'medium';
    /** Set the icon name if you need one */
    @Prop({reflect: true}) icon?: string;
    /** Left to the text or right */
    @Prop({reflect: true}) iconPosition?: 'left' | 'right' = 'left';
    /** Set the icon size if you need one */
    @Prop({reflect: true}) iconSize: Exclude<IconsSizes, 'medium' | 'xlarge' | 'large'> = 'xsmall';
    /** Set the button in loading state */
    @Prop({reflect: true}) loading = false;
    /**
     * If you only need a button with an icon. To keep your component accessible, you can give a text as slot, it will be used as title and aria-label.
     * This property takes effect only if icon property is set
     * */
    @Prop({reflect: true}) circle = false;

    private timeout!: ReturnType<typeof setTimeout>;
    private btnEl!: HTMLElement;

    /**
     * Allows to display the spinner while asynchronous tasks are pending
     * @param {Boolean} loading - loading status
     * @param {Number} timeout - if you want to display the loader during a specific timeout
     */
    @Method()
    async buttonLoading(loading: boolean, timeout = 400) {
        /**
         * To be able to actually see the loader, even for fast internets, we need to set a minimum lifetime
         * But we set a timeout only to hide the spinner, not to show it
         */
        setTimeout(
            () => {
                this.loading = loading;
                this.disabled = loading;
            },
            loading ? 0 : timeout,
        );
    }

    private get buttonColorClass() {
        return {['joy-button_' + this.variant]: this.variant};
    }

    private get buttonSizeClass() {
        return {['joy-button_' + this.size]: this.size};
    }

    private get spinnerColor() {
        if (this.variant !== 'white' && this.variant !== 'ghost' && this.variant !== 'secondary') {
            return 'white';
        }
        return 'teal';
    }

    private getLabel() {
        if (this.circle) {
            return this.el.textContent ?? '';
        }

        return undefined;
    }

    private assignRef = (el: HTMLElement) => {
        this.form && el.setAttribute('form', this.form);
        this.btnEl = el;
    };

    private onClick = (e: MouseEvent) => {
        handleRippleEffect(e, this.btnEl);
    };

    render() {
        const {disabled, download, href, icon, iconPosition, iconSize, loading, rel, spinnerColor, target, type} = this;
        const TagType = href === undefined ? 'button' : ('a' as any);

        const attrs =
            TagType === 'button'
                ? {
                      type,
                  }
                : {
                      download,
                      href,
                      rel,
                      target,
                  };

        return (
            <Host aria-disabled={disabled ? 'true' : null}>
                <TagType
                    ref={this.assignRef}
                    {...attrs}
                    disabled={disabled || loading}
                    title={this.getLabel()}
                    ariaLabel={this.getLabel()}
                    class={{
                        'joy-button': true,
                        'joy-button_loading': this.loading,
                        'joy-button_circle': this.circle,
                        ...this.buttonColorClass,
                        ...this.buttonSizeClass,
                    }}
                    onClick={this.onClick}
                >
                    <span class="joy-ripple-effect"></span>

                    {icon && iconPosition === 'left' && (
                        <joy-icon class="joy-button_icon joy-button_icon--left" name={icon} size={iconSize}></joy-icon>
                    )}
                    {loading && <joy-spinner color={spinnerColor}></joy-spinner>}
                    <span class="joy-button--slot">
                        <slot />
                    </span>
                    {icon && iconPosition === 'right' && (
                        <joy-icon class="joy-button_icon joy-button_icon--right" name={icon} size={iconSize}></joy-icon>
                    )}
                </TagType>
            </Host>
        );
    }
}
